import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
} from '@chakra-ui/react';

import DateField from 'components/fields/DateField';
import {
  parseDateString,
  formatDateToString,
} from 'components/fields/auto/resolvers/resolverUtils';
import PostalCodeInput from './PostalCodeInput';

const SUPPORTED_PROVINCE_OPTIONS = [
  { value: 1, label: 'Ontario' },
  { value: 2, label: 'Alberta' },
  { value: 5, label: 'New Brunswick' },
  { value: 7, label: 'Nova Scotia' },
];

export interface CustomerFormData {
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  email: string | null;
  home_phone: string;
  mobile_phone: string;
  street_address: string;
  city: string;
  province: number | null;
  postal_code: string;
}

interface CustomerFormProps {
  data: CustomerFormData;
  onChange: (data: CustomerFormData) => void;
}

const isValidEmail = (email: string | null): boolean => {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const isValidPhone = (phone: string): boolean => {
  if (!phone) return true;
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 11;
};

const CustomerForm = ({ data, onChange }: CustomerFormProps) => {
  const handleChange = (
    field: keyof CustomerFormData,
    value: string | number | null,
  ) => {
    onChange({ ...data, [field]: value });
  };

  const handleDateChange = (date: Date | null) => {
    onChange({ ...data, date_of_birth: formatDateToString(date) });
  };

  const emailInvalid = !isValidEmail(data.email);
  const homePhoneInvalid = !!data.home_phone && !isValidPhone(data.home_phone);
  const mobilePhoneInvalid =
    !!data.mobile_phone && !isValidPhone(data.mobile_phone);

  return (
    <Box>
      <Flex gap={3} mb={4}>
        <FormControl flex={1} isRequired>
          <FormLabel>First Name</FormLabel>
          <Input
            variant="main"
            size="sm"
            value={data.first_name}
            onChange={(e) => handleChange('first_name', e.target.value)}
          />
        </FormControl>
        <FormControl flex={1} isRequired>
          <FormLabel>Last Name</FormLabel>
          <Input
            variant="main"
            size="sm"
            value={data.last_name}
            onChange={(e) => handleChange('last_name', e.target.value)}
          />
        </FormControl>
      </Flex>

      <FormControl mb={4} isRequired isInvalid={emailInvalid}>
        <FormLabel>Email</FormLabel>
        <Input
          variant="main"
          size="sm"
          type="email"
          value={data.email || ''}
          onChange={(e) => handleChange('email', e.target.value || null)}
        />
        <FormErrorMessage color="red.600">
          Enter a valid email address
        </FormErrorMessage>
      </FormControl>

      <Box mb={4}>
        <DateField
          label="Date of Birth"
          date={parseDateString(data.date_of_birth)}
          onChange={handleDateChange}
          isClearable
        />
      </Box>

      <FormControl mb={4}>
        <FormLabel>Street Address</FormLabel>
        <Input
          variant="main"
          size="sm"
          value={data.street_address}
          onChange={(e) => handleChange('street_address', e.target.value)}
        />
      </FormControl>

      <Flex gap={3} mb={4}>
        <FormControl flex={1}>
          <FormLabel>City</FormLabel>
          <Input
            variant="main"
            size="sm"
            value={data.city}
            onChange={(e) => handleChange('city', e.target.value)}
          />
        </FormControl>
        <FormControl flex={1}>
          <FormLabel>Province</FormLabel>
          <Select
            variant="main"
            size="sm"
            value={data.province || ''}
            onChange={(e) =>
              handleChange(
                'province',
                e.target.value ? parseInt(e.target.value, 10) : null,
              )
            }
          >
            <option value="">Select...</option>
            {SUPPORTED_PROVINCE_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Select>
        </FormControl>
      </Flex>

      <Box mb={4}>
        <PostalCodeInput
          value={data.postal_code}
          onChange={(value) => handleChange('postal_code', value)}
        />
      </Box>

      <FormControl mb={4} isInvalid={homePhoneInvalid}>
        <FormLabel>Primary Phone</FormLabel>
        <Input
          variant="main"
          size="sm"
          value={data.home_phone}
          onChange={(e) => handleChange('home_phone', e.target.value)}
        />
        <FormErrorMessage>Enter a valid phone number</FormErrorMessage>
      </FormControl>

      <FormControl mb={4} isInvalid={mobilePhoneInvalid}>
        <FormLabel>Mobile Phone</FormLabel>
        <Input
          variant="main"
          size="sm"
          value={data.mobile_phone}
          onChange={(e) => handleChange('mobile_phone', e.target.value)}
        />
        <FormErrorMessage>Enter a valid phone number</FormErrorMessage>
      </FormControl>
    </Box>
  );
};

export const getEmptyCustomerFormData = (): CustomerFormData => ({
  first_name: '',
  last_name: '',
  date_of_birth: null,
  email: null,
  home_phone: '',
  mobile_phone: '',
  street_address: '',
  city: '',
  province: null,
  postal_code: '',
});

export const isCustomerFormValid = (data: CustomerFormData): boolean => {
  const hasRequiredFields =
    data.first_name.trim() !== '' && data.last_name.trim() !== '';
  const emailValid = isValidEmail(data.email);
  const homePhoneValid = !data.home_phone || isValidPhone(data.home_phone);
  const mobilePhoneValid =
    !data.mobile_phone || isValidPhone(data.mobile_phone);

  return hasRequiredFields && emailValid && homePhoneValid && mobilePhoneValid;
};

export default CustomerForm;
